import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';

/**
 * TimesheetDrilldown — Popover-style dialog that shows the underlying
 * clock events aggregated into a Payroll Center "Xh" cell.
 *
 * Rendered as a portal-like modal (uses .modal-overlay + .modal so it
 * plays nicely with the existing app CSS) triggered by clicking the
 * hours cell in the payroll table.
 *
 * Props
 *   open           — boolean; controls visibility
 *   onClose        — callback when the user dismisses the popover
 *   staffId        — staff_id used to query /api/clock-events
 *   staffName      — display name for the header
 *   startDate      — ISO date (YYYY-MM-DD) — inclusive lower bound
 *   endDate        — ISO date (YYYY-MM-DD) — inclusive upper bound
 *   totalHours     — the "Xh" figure from the payroll row (for reconciliation)
 */
export function TimesheetDrilldown({
  open,
  onClose,
  staffId,
  staffName,
  startDate,
  endDate,
  totalHours,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [events, setEvents] = useState([]);

  useEffect(() => {
    if (!open || !staffId) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setEvents([]);

    // The clock-events endpoint accepts ISO-format datetimes. We anchor
    // to midnight-inclusive so the whole start & end days are covered
    // even for late-night shifts.
    const startIso = startDate ? `${startDate}T00:00:00Z` : undefined;
    const endIso = endDate ? `${endDate}T23:59:59Z` : undefined;

    (async () => {
      try {
        const data = await api.getClockEvents(staffId, startIso, endIso);
        if (cancelled) return;
        // Sort chronological (oldest → newest) so pairs are readable.
        const sorted = [...(data || [])].sort(
          (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
        setEvents(sorted);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load clock events');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, staffId, startDate, endDate]);

  // Pair clock-in/out events chronologically so admins can see the exact
  // sessions that made up the total. Any dangling clock-in (no matching
  // clock-out) is flagged so admins can chase the missing punch-out
  // before payroll runs.
  const shifts = useMemo(() => {
    const rows = [];
    let openIn = null;
    for (const e of events) {
      const type = (e.event_type || e.type || '').toLowerCase();
      if (type === 'clock_in' || type === 'in') {
        // If a prior in wasn't closed, push it as an "open" shift and
        // start tracking the new one.
        if (openIn) {
          rows.push({ clockIn: openIn, clockOut: null });
        }
        openIn = e;
      } else if (type === 'clock_out' || type === 'out') {
        if (openIn) {
          rows.push({ clockIn: openIn, clockOut: e });
          openIn = null;
        } else {
          // Orphan clock-out (rare — probably a data glitch).
          rows.push({ clockIn: null, clockOut: e });
        }
      }
    }
    // Dangling final clock-in (still on shift or missed punch-out).
    if (openIn) {
      rows.push({ clockIn: openIn, clockOut: null });
    }
    return rows;
  }, [events]);

  const totalComputed = useMemo(() => {
    let hours = 0;
    for (const s of shifts) {
      if (s.clockIn && s.clockOut) {
        const ms = new Date(s.clockOut.timestamp).getTime() - new Date(s.clockIn.timestamp).getTime();
        const rawHours = ms / 3_600_000;
        const breakMin =
          Number(s.clockOut.break_minutes || s.clockIn.break_minutes || 0);
        hours += Math.max(0, rawHours - breakMin / 60);
      }
    }
    return hours;
  }, [shifts]);

  const totalOnRow = Number(totalHours || 0);
  const drift = Math.abs(totalComputed - totalOnRow);
  const hasDrift = drift > 0.01;

  if (!open) return null;

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      data-testid="timesheet-drilldown-overlay"
    >
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 720, width: '100%' }}
        data-testid="timesheet-drilldown-modal"
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <div>
            <h2 style={{ margin: 0 }} data-testid="timesheet-drilldown-title">
              🕒 Timesheet — {staffName || 'Staff'}
            </h2>
            <small style={{ color: 'hsl(var(--muted-foreground))' }}>
              {startDate} → {endDate} · Payroll total: <strong>{totalOnRow.toFixed(2)}h</strong>
              {hasDrift && (
                <>
                  {' · '}
                  <span
                    style={{ color: 'hsl(35 80% 35%)', fontWeight: 600 }}
                    data-testid="timesheet-drilldown-drift"
                    title="Sum of shifts below differs from the row total (e.g. missing clock-out, or approval adjustments)."
                  >
                    ⚠ Events sum to {totalComputed.toFixed(2)}h
                  </span>
                </>
              )}
            </small>
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClose}
            aria-label="Close timesheet drill-down"
            data-testid="timesheet-drilldown-close"
          >
            ✕
          </button>
        </div>

        {loading ? (
          <div className="loading" data-testid="timesheet-drilldown-loading" style={{ padding: '1.5rem 0' }}>
            Loading clock events…
          </div>
        ) : error ? (
          <div
            className="alert alert-error"
            data-testid="timesheet-drilldown-error"
            style={{
              background: 'hsl(0 70% 95%)',
              color: 'hsl(0 70% 35%)',
              padding: '0.75rem',
              borderRadius: 6,
              margin: '0.75rem 0',
            }}
          >
            {error}
          </div>
        ) : shifts.length === 0 ? (
          <div
            style={{
              padding: '1.5rem',
              textAlign: 'center',
              color: 'hsl(var(--muted-foreground))',
              border: '1px dashed hsl(var(--border))',
              borderRadius: 'var(--radius)',
              margin: '0.75rem 0',
            }}
            data-testid="timesheet-drilldown-empty"
          >
            No clock events in this period. If the row shows hours, they may have
            been entered manually — check the timesheet approvals.
          </div>
        ) : (
          <div style={{ maxHeight: '55vh', overflowY: 'auto' }} data-testid="timesheet-drilldown-list">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid hsl(var(--border))', textAlign: 'left' }}>
                  <th style={cellHead}>#</th>
                  <th style={cellHead}>Clock in</th>
                  <th style={cellHead}>Clock out</th>
                  <th style={{ ...cellHead, textAlign: 'right' }}>Break</th>
                  <th style={{ ...cellHead, textAlign: 'right' }}>Net hours</th>
                  <th style={cellHead}>Location</th>
                  <th style={cellHead}>Status</th>
                </tr>
              </thead>
              <tbody>
                {shifts.map((s, i) => {
                  const inTs = s.clockIn?.timestamp;
                  const outTs = s.clockOut?.timestamp;
                  const rawMs = inTs && outTs
                    ? new Date(outTs).getTime() - new Date(inTs).getTime()
                    : null;
                  const breakMin = Number(s.clockOut?.break_minutes || s.clockIn?.break_minutes || 0);
                  const netH = rawMs != null ? Math.max(0, rawMs / 3_600_000 - breakMin / 60) : null;
                  const loc = s.clockIn?.location || s.clockOut?.location || '—';
                  // Approval status: prefer the clock-out's approval as
                  // it's the row that closes the shift.
                  const closer = s.clockOut || s.clockIn;
                  const approvalRaw = closer?.approval_status || closer?.status;
                  const isAuto = closer?.auto_approved === true || approvalRaw === 'auto_approved';
                  const missingOut = !!s.clockIn && !s.clockOut;
                  return (
                    <tr key={i} style={{ borderTop: '1px solid hsl(var(--border))' }} data-testid={`timesheet-drilldown-row-${i}`}>
                      <td style={cell}>{i + 1}</td>
                      <td style={cell}>{inTs ? new Date(inTs).toLocaleString() : <em style={{ color: 'hsl(0 60% 40%)' }}>missing</em>}</td>
                      <td style={cell}>
                        {outTs ? (
                          new Date(outTs).toLocaleString()
                        ) : (
                          <em style={{ color: 'hsl(0 60% 40%)' }} data-testid={`timesheet-drilldown-missing-out-${i}`}>
                            ⚠ still open
                          </em>
                        )}
                      </td>
                      <td style={{ ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {breakMin > 0 ? `${breakMin}m` : '—'}
                      </td>
                      <td style={{ ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                        {netH != null ? `${netH.toFixed(2)}h` : '—'}
                      </td>
                      <td style={cell}>{loc}</td>
                      <td style={cell}>
                        {missingOut ? (
                          <span className="badge" style={{ background: 'hsl(0 75% 94%)', color: 'hsl(0 60% 35%)', fontSize: '0.72rem' }}>
                            No clock-out
                          </span>
                        ) : isAuto ? (
                          <span className="badge" style={{ background: 'hsl(140 60% 92%)', color: 'hsl(140 55% 26%)', fontSize: '0.72rem' }}>
                            Auto-approved
                          </span>
                        ) : approvalRaw ? (
                          <span className="badge" style={{ background: 'hsl(220 60% 94%)', color: 'hsl(220 60% 30%)', fontSize: '0.72rem' }}>
                            {String(approvalRaw).replace(/_/g, ' ')}
                          </span>
                        ) : (
                          <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.72rem' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.75rem' }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onClose}
            data-testid="timesheet-drilldown-done"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

const cell = { padding: '0.5rem 0.5rem', verticalAlign: 'top' };
const cellHead = { padding: '0.5rem 0.5rem', fontWeight: 600, fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.03em' };

export default TimesheetDrilldown;
